import React, { useState } from 'react';
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Building2, 
  CheckCircle2, 
  AlertTriangle, 
  ArrowRight, 
  LogIn, 
  UserPlus, 
  Mail, 
  ShieldCheck 
} from 'lucide-react';
import { apiWorker, acceptInvitation } from '../api/worker';
import { useCurrentUser } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { PublicHeader } from '../components/shell/PublicHeader';
import { PublicFooter } from '../components/shell/PublicFooter';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Spinner } from '../components/ui/Spinner';
import { SEO } from '../components/SEO';
import toast from 'react-hot-toast';

export const JoinPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { token: routeToken } = useParams<{ token?: string }>();
  const token = searchParams.get('token') || routeToken || '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, memberships, isSignedIn, isLoading: isAuthLoading } = useCurrentUser();
  const { switchOrg } = useTenant();

  const [hasAccepted, setHasAccepted] = useState(false);

  // Validate invitation token
  const { data: invitation, isLoading: isValidating, isError } = useQuery({
    queryKey: ['invitation-validate', token],
    queryFn: async () => {
      if (!token) throw new Error('Missing token');
      const res = await apiWorker.get(`/api/invitations/validate/${token}`);
      return res.data;
    },
    enabled: !!token,
    retry: 1
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const firstName = user?.firstName || '';
      const lastName = user?.lastName || '';
      return acceptInvitation({ token, firstName, lastName });
    },
    onSuccess: (data: any) => {
      setHasAccepted(true);
      toast.success(`Welcome to ${invitation?.nodeName || 'the organization'}!`);
      queryClient.invalidateQueries({ queryKey: ['my-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['user-memberships-live'] });
      queryClient.invalidateQueries({ queryKey: ['node-config-full'] });

      // Target slug from response, invitation, or memberships lookup
      let targetSlug = data?.nodeSlug || invitation?.nodeSlug;
      if (!targetSlug && invitation?.nodeId) {
        const matched = memberships?.find(m => m.nodeId === invitation.nodeId || m.id === invitation.nodeId);
        if (matched?.nodeSlug) {
          targetSlug = matched.nodeSlug;
        }
      }

      if (targetSlug) {
        switchOrg(targetSlug, '/dashboard');
      } else {
        navigate('/dashboard');
      }
    },
    onError: (err: any) => {
      const message = err?.response?.data?.message || 'Failed to accept invitation. Please try again.';
      toast.error(message);
    }
  });

  const redirectUrl = `/join?token=${encodeURIComponent(token)}`;

  return (
    <div className="min-h-screen bg-[#F5F5F0] text-black dark:bg-black dark:text-white flex flex-col justify-between">
      <SEO 
        title={invitation?.nodeName ? `Join ${invitation.nodeName} | OrgSets` : 'Join Organization | OrgSets'}
        description="Accept your official invitation to join an organization on OrgSets."
      />
      <PublicHeader />

      <main className="flex-1 flex items-center justify-center p-4 pt-28 pb-16">
        <div className="w-full max-w-2xl">
          {/* Missing Token */}
          {!token && (
            <div className="bg-white dark:bg-slate-900 border-8 border-black dark:border-white shadow-[12px_12px_0px_#000000] dark:shadow-[12px_12px_0px_#FFFFFF] p-8 md:p-12 text-center space-y-6">
              <div className="w-16 h-16 bg-[#FFD600] border-4 border-black mx-auto flex items-center justify-center">
                <AlertTriangle size={32} className="text-black" />
              </div>
              <h1 className="text-3xl md:text-5xl font-black font-display uppercase tracking-tight text-black dark:text-white">
                Missing Invite Token
              </h1>
              <p className="text-sm font-mono text-gray-600 dark:text-gray-300 max-w-md mx-auto leading-relaxed">
                This invitation link appears to be incomplete. Please check your invitation email and click the full link.
              </p>
              <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/"
                  className="w-full sm:w-auto inline-flex items-center justify-center uppercase font-bold tracking-widest text-xs py-4 px-8 border-4 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white hover:bg-[#FFD600] dark:hover:bg-[#FFD600] dark:hover:text-black rounded-none shadow-[4px_4px_0px_#000] transition-all font-mono"
                >
                  Return Home
                </Link>
                <Link 
                  to="/sign-in"
                  className="w-full sm:w-auto inline-flex items-center justify-center uppercase font-bold tracking-widest text-xs py-4 px-8 bg-black text-white hover:bg-[#0044FF] border-4 border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000] transition-all font-mono"
                >
                  Sign In
                </Link>
              </div>
            </div>
          )}

          {/* Validating State */}
          {token && isValidating && (
            <div className="bg-white dark:bg-slate-900 border-8 border-black dark:border-white shadow-[12px_12px_0px_#0044FF] p-12 text-center space-y-6">
              <div className="flex justify-center">
                <Spinner size="lg" />
              </div>
              <h2 className="text-2xl font-black font-display uppercase tracking-tight text-black dark:text-white">
                Verifying Invitation...
              </h2>
              <p className="text-xs font-mono text-gray-500 uppercase tracking-widest">
                Contacting OrgSets security gateway
              </p>
            </div>
          )}

          {/* Invalid / Expired Token */}
          {token && !isValidating && (isError || invitation?.status === 'EXPIRED' || invitation?.status === 'REVOKED') && (
            <div className="bg-white dark:bg-slate-900 border-8 border-black dark:border-white shadow-[12px_12px_0px_#FFD600] p-8 md:p-12 text-center space-y-6">
              <div className="w-16 h-16 bg-red-600 border-4 border-black mx-auto flex items-center justify-center text-white">
                <AlertTriangle size={32} />
              </div>
              <h1 className="text-3xl md:text-5xl font-black font-display uppercase tracking-tight text-black dark:text-white">
                Invitation {invitation?.status === 'EXPIRED' ? 'Expired' : 'Invalid'}
              </h1>
              <p className="text-sm font-mono text-gray-600 dark:text-gray-300 max-w-md mx-auto leading-relaxed">
                This invitation is no longer active. It may have expired or been revoked by the organization administrator. Please request a new invitation from your organization.
              </p>
              <div className="pt-4 flex flex-col sm:flex-row gap-4 justify-center">
                <Link 
                  to="/"
                  className="w-full sm:w-auto inline-flex items-center justify-center uppercase font-bold tracking-widest text-xs py-4 px-8 border-4 border-black dark:border-white bg-white dark:bg-black text-black dark:text-white hover:bg-[#FFD600] dark:hover:bg-[#FFD600] dark:hover:text-black rounded-none shadow-[4px_4px_0px_#000] transition-all font-mono"
                >
                  Return Home
                </Link>
                <Link 
                  to="/contact"
                  className="w-full sm:w-auto inline-flex items-center justify-center uppercase font-bold tracking-widest text-xs py-4 px-8 bg-black text-white hover:bg-[#0044FF] border-4 border-black dark:border-white rounded-none shadow-[4px_4px_0px_#000] transition-all font-mono"
                >
                  Contact Support
                </Link>
              </div>
            </div>
          )}

          {/* Valid Invitation */}
          {token && !isValidating && invitation && invitation.status === 'PENDING' && (
            <div className="bg-white dark:bg-black border-8 border-black dark:border-white shadow-[16px_16px_0px_#0044FF] dark:shadow-[16px_16px_0px_#FFD600] overflow-hidden">
              {/* Top Bauhaus Banner */}
              <div className="bg-black text-white px-6 py-4 flex items-center justify-between border-b-4 border-black dark:border-white">
                <div className="flex items-center gap-2.5">
                  <div className="w-3 h-3 bg-[#FFD600]" />
                  <span className="font-mono text-xs uppercase tracking-widest font-black">
                    Official Organization Invitation
                  </span>
                </div>
                <Badge className="bg-[#0044FF] text-white rounded-none border-2 border-white font-mono uppercase tracking-widest text-[10px]">
                  Verified Token
                </Badge>
              </div>

              {/* Main Content */}
              <div className="p-8 md:p-12 space-y-8">
                <div className="flex items-start gap-5">
                  <div className="w-16 h-16 bg-[#0044FF] border-4 border-black text-white flex items-center justify-center shrink-0">
                    <Building2 size={32} />
                  </div>
                  <div>
                    <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-gray-400">
                      You're Invited to Join
                    </span>
                    <h1 className="text-3xl md:text-5xl font-black font-display uppercase tracking-tight text-black dark:text-white leading-tight">
                      {invitation.nodeName || 'Organization'}
                    </h1>
                  </div>
                </div>

                {/* Role and Details Card */}
                <div className="bg-[#F5F5F0] dark:bg-slate-900 border-4 border-black dark:border-white p-6 space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-black/10 dark:border-white/10 pb-3">
                    <span className="text-xs font-mono font-bold uppercase tracking-widest text-gray-500">
                      Assigned Role
                    </span>
                    <Badge variant="blue" className="text-xs px-3 py-1 uppercase font-bold tracking-wider">
                      {invitation.role || 'MEMBER'}
                    </Badge>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 border-b-2 border-black/10 dark:border-white/10 pb-3">
                    <span className="text-xs font-mono font-bold uppercase tracking-widest text-gray-500 flex items-center gap-1.5">
                      <Mail size={14} /> Invited Recipient
                    </span>
                    <span className="font-mono font-bold text-xs text-black dark:text-white">
                      {invitation.email}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 pt-1 text-emerald-600 dark:text-emerald-400 text-xs font-mono">
                    <ShieldCheck size={16} />
                    <span>Access to calendars, pulse broadcasts, discussions & member directory</span>
                  </div>
                </div>

                {/* Acceptance Flow */}
                {isAuthLoading ? (
                  <div className="py-8 flex justify-center">
                    <Spinner size="md" />
                  </div>
                ) : isSignedIn ? (
                  <div className="space-y-4 pt-2">
                    <div className="bg-blue-50 dark:bg-slate-900 border-2 border-[#0044FF] p-4 text-xs font-mono text-gray-700 dark:text-gray-300">
                      Signed in as <strong className="text-black dark:text-white">{user?.fullName || user?.email}</strong>. Accepting will instantly bind your profile to <strong>{invitation.nodeName || 'this organization'}</strong>.
                    </div>

                    <Button
                      onClick={() => acceptMutation.mutate()}
                      isLoading={acceptMutation.isPending}
                      className="w-full bg-black text-white hover:bg-[#0044FF] dark:bg-white dark:text-black dark:hover:bg-[#0044FF] dark:hover:text-white font-black uppercase tracking-widest py-6 text-sm rounded-none border-4 border-black dark:border-white shadow-[6px_6px_0px_#0044FF] transition-all flex items-center justify-center gap-2"
                    >
                      <CheckCircle2 size={18} />
                      Accept Invitation & Enter Dashboard
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4 pt-2">
                    <p className="text-xs font-mono text-gray-600 dark:text-gray-400 leading-relaxed">
                      To complete your registration and claim this role, please sign up or sign in to your OrgSets account.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <Link 
                        to={`/sign-up?redirect_url=${encodeURIComponent(redirectUrl)}`} 
                        className="w-full bg-[#0044FF] text-white hover:bg-black font-black uppercase tracking-widest py-5 text-xs rounded-none border-4 border-black dark:border-white shadow-[4px_4px_0px_#000000] inline-flex items-center justify-center gap-2 font-mono transition-all"
                      >
                        <UserPlus size={16} /> Sign Up to Join
                      </Link>

                      <Link 
                        to={`/sign-in?redirect_url=${encodeURIComponent(redirectUrl)}`} 
                        className="w-full bg-white text-black hover:bg-[#FFD600] font-black uppercase tracking-widest py-5 text-xs rounded-none border-4 border-black dark:border-white shadow-[4px_4px_0px_#000000] inline-flex items-center justify-center gap-2 font-mono transition-all"
                      >
                        <LogIn size={16} /> Already Have Account
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Already Accepted */}
          {token && !isValidating && invitation && (invitation.status === 'ACCEPTED' || hasAccepted) && (
            <div className="bg-white dark:bg-black border-8 border-black dark:border-white shadow-[16px_16px_0px_#059669] p-8 md:p-12 text-center space-y-6">
              <div className="w-16 h-16 bg-emerald-600 border-4 border-black mx-auto flex items-center justify-center text-white">
                <CheckCircle2 size={32} />
              </div>
              <h1 className="text-3xl md:text-5xl font-black font-display uppercase tracking-tight text-black dark:text-white">
                Invitation Accepted!
              </h1>
              <p className="text-sm font-mono text-gray-600 dark:text-gray-300 max-w-md mx-auto leading-relaxed">
                You are now an active member of <strong>{invitation.nodeName || 'your organization'}</strong>.
              </p>
              <div className="pt-4 flex justify-center">
                <Button 
                  onClick={() => navigate('/dashboard')}
                  className="bg-black text-white hover:bg-[#0044FF] uppercase font-bold tracking-widest text-xs py-5 px-10 border-4 border-black rounded-none shadow-[4px_4px_0px_#0044FF]"
                >
                  Enter Dashboard <ArrowRight size={16} className="ml-2" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};
